import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useWorkflowRunService } from '../services/workflowRunService';
import {
  branchTakenMessage,
  gateResolvedMessage,
  runAbandonedMessage,
  stepRetriedMessage,
  workflowStartedMessage,
} from '../utils/agentMessages';
import type {
  WorkflowCatalogEntry,
  WorkflowRunDetail,
  WorkflowRunSummary,
} from '../../../types/backend';

export const workflowRunKeys = {
  runs: (customerId?: string, investigationId?: string) =>
    ['workflow-runs', customerId, investigationId] as const,
  runnable: (customerId?: string) => ['workflow-runnable', customerId] as const,
  run: (customerId?: string, investigationId?: string, runId?: string) =>
    ['workflow-run', customerId, investigationId, runId] as const,
};

interface Options {
  customerId: string | undefined;
  investigationId: string | undefined;
  /**
   * Sends a chat turn to the investigation's agent. Starting a run and answering a gate are RPC
   * writes the agent never sees, so without this it does not act on either.
   */
  tellAgent?: (message: string) => Promise<void>;
  /**
   * False while workflows is switched off: the catalogue of workflows to start is not read and
   * nothing starts, while the runs under way are still listed and read (FS-UC-02 3a).
   */
  startable?: boolean;
}

/**
 * Run state for one investigation: the summaries the sidebar lists, the catalogue the launcher
 * offers, and the one run the modal has open.
 *
 * Reads go through the query layer (FE-13, ADR-0050), which owns caching, dedup and
 * revalidation. Writes invalidate rather than patching the cache locally: the agent advances the
 * same runs from its own tools, so a locally-derived list would drift from the server's within one
 * chat turn, and a stale gate badge is worse than a slow one.
 */
export const useWorkflowRuns = ({ customerId, investigationId, tellAgent, startable = true }: Options) => {
  const service = useWorkflowRunService();
  const queryClient = useQueryClient();
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [agentNoticeError, setAgentNoticeError] = useState<string | null>(null);

  const enabled = Boolean(customerId && investigationId);

  const runsQuery = useQuery<WorkflowRunSummary[]>({
    queryKey: workflowRunKeys.runs(customerId, investigationId),
    queryFn: () => service.listRuns(customerId as string, investigationId as string),
    enabled,
  });

  const runnableQuery = useQuery<WorkflowCatalogEntry[]>({
    queryKey: workflowRunKeys.runnable(customerId),
    queryFn: () => service.listRunnable(customerId as string),
    enabled: startable && Boolean(customerId),
  });

  const detailQuery = useQuery<WorkflowRunDetail>({
    queryKey: workflowRunKeys.run(customerId, investigationId, openRunId ?? undefined),
    queryFn: () =>
      service.getRun(customerId as string, investigationId as string, openRunId as string),
    enabled: enabled && Boolean(openRunId),
  });

  const invalidateRuns = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: workflowRunKeys.runs(customerId, investigationId),
      }),
    [queryClient, customerId, investigationId],
  );

  // Memoised: every write refreshes through it. Takes the run it wrote to rather than reading
  // the open one, because the sidebar row acts on its own run whether or not that run is open.
  const invalidateRun = useCallback(
    (runId: string | null) =>
      Promise.all([
        invalidateRuns(),
        queryClient.invalidateQueries({
          queryKey: workflowRunKeys.run(customerId, investigationId, runId ?? undefined),
        }),
      ]),
    [invalidateRuns, queryClient, customerId, investigationId],
  );

  const invalidateOpenRun = useCallback(
    () => invalidateRun(openRunId),
    [invalidateRun, openRunId],
  );

  const startMutation = useMutation({
    mutationFn: (workflowName: string) =>
      service.startWorkflow(customerId as string, investigationId as string, workflowName),
    onSuccess: invalidateRuns,
  });

  const resolveGateMutation = useMutation({
    mutationFn: ({
      nodeId,
      approved,
      note,
    }: {
      nodeId: string;
      approved: boolean;
      note?: string;
    }) =>
      service.resolveGate(
        customerId as string,
        investigationId as string,
        openRunId as string,
        nodeId,
        approved,
        note,
      ),
    // Both: approving can advance the run, so the row and the open modal would otherwise disagree.
    onSuccess: () => invalidateOpenRun(),
  });

  const takeBranchMutation = useMutation({
    mutationFn: ({ nodeId, condition }: { nodeId: string; condition: string }) =>
      service.takeBranch(
        customerId as string,
        investigationId as string,
        openRunId as string,
        nodeId,
        condition,
        'Answered by the operator',
      ),
    // Taking a branch prunes the routes not taken and moves the run, so the row and the open
    // drawing would otherwise disagree about where it stands.
    onSuccess: () => invalidateOpenRun(),
  });

  // A retry unblocks the run and an abandon ends it; either changes the row and the open modal.
  // Both name their run, because both are offered on the sidebar row of a run nobody has opened.
  const retryMutation = useMutation({
    mutationFn: ({ runId, nodeId }: { runId: string; nodeId: string }) =>
      service.retryStep(customerId as string, investigationId as string, runId, nodeId),
    onSuccess: (_data, { runId }) => invalidateRun(runId),
  });

  const abandonMutation = useMutation({
    mutationFn: ({ runId, reason }: { runId: string; reason: string }) =>
      service.abandonRun(customerId as string, investigationId as string, runId, reason),
    onSuccess: (_data, { runId }) => invalidateRun(runId),
  });

  // The write already succeeded when this runs, so a failure here is reported on its own rather
  // than thrown back at the caller: the launcher would show it as a failed start and invite a
  // second, duplicate run. Memoised so the two callbacks below keep their identity.
  const notifyAgent = useCallback(
    async (compose: () => string) => {
      if (!tellAgent) return;
      setAgentNoticeError(null);
      try {
        await tellAgent(compose());
      } catch (e) {
        const reason = e instanceof Error ? e.message : 'the message did not send';
        setAgentNoticeError(`The agent was not told: ${reason}`);
      }
    },
    [tellAgent],
  );

  const resolveGateAsync = resolveGateMutation.mutateAsync;
  // Memoised: the modal is memo()ed and takes this as a prop.
  const resolveGate = useCallback(
    async (nodeId: string, approved: boolean, note?: string) => {
      if (!openRunId) return;
      await resolveGateAsync({ nodeId, approved, note });
      await notifyAgent(() => {
        const step = detailQuery.data?.plan?.nodes.find((n) => n.id === nodeId);
        if (!step) throw new Error(`step ${nodeId} is not in run ${openRunId}`);
        return gateResolvedMessage(step.label, openRunId, approved, note);
      });
    },
    [openRunId, resolveGateAsync, notifyAgent, detailQuery.data],
  );

  const takeBranchAsync = takeBranchMutation.mutateAsync;
  // Memoised: the panel is memo()ed and takes this as a prop.
  const takeBranch = useCallback(
    async (nodeId: string, condition: string) => {
      if (!openRunId) return;
      await takeBranchAsync({ nodeId, condition });
      await notifyAgent(() => {
        const step = detailQuery.data?.plan?.nodes.find((n) => n.id === nodeId);
        if (!step) throw new Error(`step ${nodeId} is not in run ${openRunId}`);
        return branchTakenMessage(step.label, openRunId, condition);
      });
    },
    [openRunId, takeBranchAsync, notifyAgent, detailQuery.data],
  );

  const retryAsync = retryMutation.mutateAsync;
  // Memoised: the modal is memo()ed and takes this as a prop.
  //
  // The caller names the run and the step's label rather than this looking either up. Reading
  // the label out of the open run's detail is what made the sidebar control impossible: a retry
  // from a row nobody had opened has no detail to read, so the write landed and the agent was
  // never told, leaving the step failed from the only side that can act on it.
  const retryStep = useCallback(
    async (runId: string, nodeId: string, stepLabel: string) => {
      await retryAsync({ runId, nodeId });
      await notifyAgent(() => stepRetriedMessage(stepLabel, runId));
    },
    [retryAsync, notifyAgent],
  );

  const abandonAsync = abandonMutation.mutateAsync;
  // Memoised: the modal is memo()ed and takes this as a prop.
  const abandonRun = useCallback(
    async (runId: string, reason: string) => {
      await abandonAsync({ runId, reason });
      await notifyAgent(() => runAbandonedMessage(runId, reason));
    },
    [abandonAsync, notifyAgent],
  );

  const startAsync = startMutation.mutateAsync;
  // Memoised: the launcher is memo()ed and takes this as a prop.
  const startWorkflow = useCallback(
    async (workflowName: string) => {
      if (!startable) throw new Error('Workflows are switched off: nothing new starts');
      const run = await startAsync(workflowName);
      await notifyAgent(() => {
        const entry = runnableQuery.data?.find((w) => w.name === workflowName);
        if (!entry) throw new Error(`workflow ${workflowName} is not in the catalogue`);
        if (!run?.run_id) throw new Error(`starting ${workflowName} returned no run id`);
        return workflowStartedMessage(entry.title, run.run_id);
      });
      // The launcher shows the start's access warnings: routes the run may reach and fail on.
      return run;
    },
    [startable, startAsync, notifyAgent, runnableQuery.data],
  );

  const asMessage = (e: unknown, fallback: string) =>
    e instanceof Error ? e.message : fallback;

  return {
    runs: runsQuery.data ?? [],
    runnable: runnableQuery.data ?? [],
    // Kept out of the two above and shown in the launcher itself: a catalogue that cannot load
    // leaves nothing to start, and without this the button is merely disabled, which reads as
    // "there is nothing to run" (PRIN-06). It is not an investigation-level error either, which
    // would blame the investigation for a listing that failed.
    runnableError: runnableQuery.error
      ? asMessage(runnableQuery.error, 'Could not load the workflows that can be run here')
      : null,
    loading: runsQuery.isLoading,
    detail: openRunId ? (detailQuery.data ?? null) : null,
    detailLoading: detailQuery.isLoading,
    // Kept apart because they are shown in different places: a listing that failed belongs in the
    // sidebar, where its absence would otherwise read as "no runs", and a detail that failed
    // belongs in the modal the operator opened. One combined string would put each in both.
    runsError: runsQuery.error ? asMessage(runsQuery.error, 'Could not load workflow runs') : null,
    detailError:
      openRunId && detailQuery.error
        ? asMessage(detailQuery.error, 'Could not load that workflow run')
        : null,
    // The dialog opens on this, not on `detail`. Opening on the data would keep it shut for the
    // whole fetch, so a cold run looks like a click that did nothing and the modal's own loading
    // state could never be reached.
    openRunId,
    // Both halves: the agent moving a run changes the row AND the open run's detail, and the
    // buttons are built from the detail alone. Refreshing only the list left the row reading
    // "Waiting on you" beside a band with no Approve in it, because the cached detail still
    // held no open gate. The two queries have different key prefixes, so one invalidation
    // never reached the other.
    refresh: invalidateOpenRun,
    openRun: useCallback((runId: string) => setOpenRunId(runId), []),
    closeRun: useCallback(() => setOpenRunId(null), []),
    startWorkflow,
    resolveGate,
    takeBranch,
    retryStep,
    abandonRun,
    // Set when a write succeeded but the chat turn telling the agent about it did not.
    agentNoticeError,
    // Memoised: the dashboard's snackbar close handler depends on it.
    clearAgentNoticeError: useCallback(() => setAgentNoticeError(null), []),
  };
};
